import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "../contexts/OnboardingContext";
import { useAuth } from "../contexts/AuthContext";
import OnboardingLayout from "../components/OnboardingLayout";
import BasicInfo from "../components/OnboardingLayout/BasicInfo";
import Authority from "../components/OnboardingLayout/Authority";
import Summary from "../components/OnboardingLayout/Summary";
import { MoveLeft, MoveRight } from "lucide-react";
import axios from "axios";
import { API_URL } from "../config";
import { INTERNAL_EMAILS } from "../utils/internalEmails";
import {
  ONBOARDING_TITLES,
  ONBOARDING_SUBTITLES,
} from "../constants/onboardingCopy";
import {
  ROLE_TYPES,
  STEP_IDS,
  ONBOARDING_FLOWS,
} from "../constants/onboardingSteps";

const Onboarding = () => {
  const {
    currentStep,
    onboardingProfile,
    goToNextStep,
    goToPrevStep,
    setError,
  } = useOnboarding();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const activeFlow = ONBOARDING_FLOWS[ROLE_TYPES.CARRIER];

  // Prevent users who have already completed onboarding from accessing this page
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // User is guaranteed to exist here (ProtectedRoute handles authentication)
      if (!user) {
        return;
      }

      // Fetch profile to check if onboarding is complete (phone_number might not be in user object)
      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          return;
        }

        const response = await axios.get(`${API_URL}/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const profile = response.data;
        const hasPhoneNumber =
          profile.phone_number && profile.phone_number.trim() !== "";

        // For carriers, check if they have DOT authorization
        if (user.account_type === "carrier") {
          const hasDotAuthorized = profile.dot_authorized || false;
          // If carrier has phone_number and dot_authorized, they've completed onboarding
          if (hasPhoneNumber && hasDotAuthorized) {
            navigate("/", { replace: true });
            return;
          }
        } else if (user.account_type === "dispatcher") {
          // If dispatcher has phone_number, they've completed onboarding
          if (hasPhoneNumber) {
            navigate("/", { replace: true });
            return;
          }
        }
      } catch (err) {
        // If profile fetch fails, allow them to proceed (might be in progress)
        console.log("Could not fetch profile for onboarding check:", err);
      }
    };

    checkOnboardingStatus();
  }, [user, navigate]);

  // Determine which step component to render
  // Only required fields: Role Selection → Basic Info (phone) → Authority (DOT for carriers) → Summary
  const renderStepContent = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    const currentStepId = activeFlow[clampedStepIndex];

    switch (currentStepId) {
      case STEP_IDS.BASIC_INFO:
        return <BasicInfo />;
      case STEP_IDS.AUTHORITY:
        return <Authority />;
      case STEP_IDS.SUMMARY:
      default:
        return <Summary />;
    }
  };

  // Get step titles for the layout
  const getStepTitle = () => {
    const titles = ONBOARDING_TITLES.carrier;
    return titles[currentStep] || "Onboarding";
  };

  const getStepSubtitle = () => {
    const subtitles = ONBOARDING_SUBTITLES.carrier;
    return subtitles[currentStep] || "";
  };

  const handleFinish = async () => {
    // Minimal safety check - main validation happens at each step
    setIsSaving(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");

      if (!token) {
        console.log("TOKEN NOT FOUND");
        setError("Authentication required. Please sign in again.");
        setIsSaving(false);
        return;
      }

      // Build profile payload with all collected data
      const profilePayload = {
        email: user?.email || onboardingProfile.email || "",
        name: user?.name || onboardingProfile.name || "",
        google_id: user?.google_id || onboardingProfile.google_id || "",
        account_type: onboardingProfile.account_type,
        phone_number: onboardingProfile.phone_number,
        // Include optional fields if they were filled
        company_name: onboardingProfile.company_name || "",
        mc_number: onboardingProfile.mc_number || "",
        dot_number: onboardingProfile.dot_number || "",
        dot_authorized: onboardingProfile.dot_authorized || false,
        truck_type: onboardingProfile.truck_type || "V",
        origin_city: onboardingProfile.origin_city || "",
        truckstop_integration_id:
          onboardingProfile.truckstop_integration_id || "",
        ch_t_number: onboardingProfile.ch_t_number || "",
      };

      // Save profile to backend
      await axios.post(`${API_URL}/user/profile`, profilePayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh user data
      await refreshUser();

      localStorage.removeItem("onboarding_profile");

      // Navigate to main app
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Error saving profile:", err);
      setError(
        err.response?.data?.detail ||
          "Failed to save profile. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    const currentStepId = activeFlow[clampedStepIndex];

    // Step: Basic Info - validate phone number
    if (currentStepId === STEP_IDS.BASIC_INFO) {
      if (
        !onboardingProfile.phone_number ||
        onboardingProfile.phone_number.trim() === ""
      ) {
        setError("Phone number is required");
        return;
      }
      // Validate phone number format (10 digits)
      const phoneRegex = /^\d{10}$/;
      if (!phoneRegex.test(onboardingProfile.phone_number.trim())) {
        setError("Phone number must be exactly 10 digits");
        return;
      }
      if (!onboardingProfile.truck_type || onboardingProfile.truck_type.trim() === "") {
        setError("Truck type is required");
        return;
      }
    }

    // Step: Authority - validate DOT verification
    if (currentStepId === STEP_IDS.AUTHORITY) {
      if (
        !onboardingProfile.dot_number ||
        onboardingProfile.dot_number.trim() === ""
      ) {
        setError("DOT number is required");
        return;
      }
      // Allow internal emails to bypass verification requirement
      const isInternalEmail =
        user?.email && INTERNAL_EMAILS.includes(user.email);
      if (!onboardingProfile.dot_authorized && !isInternalEmail) {
        setError("Please verify your DOT number before continuing");
        return;
      }
    }

    // Step: Truckstop ID - show confirmation if missing
    // if (currentStepId === STEP_IDS.TRUCKSTOP) {
    //     if (!onboardingProfile.truckstop_integration_id || onboardingProfile.truckstop_integration_id.trim() === "") {
    //         setIsConfirmationModalOpen(true);
    //         return;
    //     }
    // }

    // Clear any previous errors
    setError("");

    localStorage.setItem(
      "onboarding_profile",
      JSON.stringify(onboardingProfile)
    );

    // Save to backend incrementally after critical steps
    const saveToBackend = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;

        // Build partial profile payload with current data
        const profilePayload = {
          email: user?.email || onboardingProfile.email || "",
          name: user?.name || onboardingProfile.name || "",
          google_id: user?.google_id || onboardingProfile.google_id || "",
          account_type: onboardingProfile.account_type || "carrier",
          phone_number: onboardingProfile.phone_number || "",
          company_name: onboardingProfile.company_name || "",
          mc_number: onboardingProfile.mc_number || "",
          dot_number: onboardingProfile.dot_number || "",
          dot_authorized: onboardingProfile.dot_authorized || false,
          truck_type: onboardingProfile.truck_type || "V",
          origin_city: onboardingProfile.origin_city || "",
          truckstop_integration_id: onboardingProfile.truckstop_integration_id || "",
          ch_t_number: onboardingProfile.ch_t_number || "",
        };

        // Save to backend (silently - don't show errors to user)
        await axios.post(`${API_URL}/user/profile`, profilePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (err) {
        // Silently fail - we don't want to interrupt the flow
        console.error("Error saving profile incrementally:", err);
      }
    };

    // Save after phone number step or DOT verification step
    if (currentStepId === STEP_IDS.BASIC_INFO || currentStepId === STEP_IDS.AUTHORITY) {
      saveToBackend();
    }

    // If on last step, finish setup instead of going to next step
    if (isLastStep()) {
      handleFinish();
      return;
    }

    goToNextStep();
  };

  const handleBack = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    if (clampedStepIndex > 0) {
      goToPrevStep();
    }
  };

  const isLastStep = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    return clampedStepIndex === activeFlow.length - 1;
  };

  const shouldShowBackButton = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    return clampedStepIndex > 0;
  };

  const shouldShowContinueButton = () => {
    const clampedStepIndex = Math.min(currentStep, activeFlow.length - 1);
    const currentStepId = activeFlow[clampedStepIndex];

    // Hide continue on Authority step until DOT is verified (unless internal email bypass)
    if (currentStepId === STEP_IDS.AUTHORITY) {
      const isInternalEmail =
        user?.email && INTERNAL_EMAILS.includes(user.email);
      const isDotVerified = onboardingProfile.dot_authorized || isInternalEmail;
      return isDotVerified;
    }

    return true;
  };

  const getContinueButtonText = () => {
    if (isSaving) {
      return "Saving...";
    }
    
    if (isLastStep()) {
      return "Finish Setup";
    }

    // Only BASIC_INFO and AUTHORITY now precede SUMMARY
    return "Continue";
  };

  return (
    <OnboardingLayout
      title={onboardingProfile.account_type ? getStepTitle() : ""}
      subtitle={onboardingProfile.account_type ? getStepSubtitle() : ""}
      showStepIndicator={true}
    >
      <div className="space-y-6">
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="flex gap-4 justify-between mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            disabled={!shouldShowBackButton()}
            className="flex items-center gap-1.5 py-2.5 px-4 bg-white border border-gray-200 rounded-lg text-gray-700 text-md cursor-pointer"
          >
            <MoveLeft aria-hidden="true" className="-ml-0.5 size-3" />
            Back
          </button>
          {shouldShowContinueButton() && (
            <button
              type="button"
              onClick={handleContinue}
              disabled={isSaving}
              className="flex items-center gap-1.5 py-2.5 px-4 bg-[#1E3A5F] border-0 rounded-lg text-white text-md font-medium cursor-pointer"
            >
              {isSaving ? (
                <>
                  <span>Saving...</span>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </>
              ) : (
                <>
                  {getContinueButtonText()}
                  <MoveRight aria-hidden="true" className="-mr-0.5 size-3" />
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Confirmation Modal for missing Truckstop ID */}
      {/* <SaveConfirmationModal
                isOpen={isConfirmationModalOpen}
                onClose={() => setIsConfirmationModalOpen(false)}
                onConfirm={() => {
                    setIsConfirmationModalOpen(false);
                    // Clear any previous errors
                    setError("");
                    // If on last step, finish setup, otherwise go to next step
                    if (isLastStep()) {
                        handleFinish();
                    } else {
                        goToNextStep();
                    }
                }}
            /> */}
    </OnboardingLayout>
  );
};

export default Onboarding;
